import AusCart from "@aus/Assets/Icons/your_cart_white";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Link from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React, { FC, useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, withTheme } from "styled-components";

const enum fields {
    visibilityState = "visibilityState",
    icon = "icon",
}

const mapStateToProps = (state: ApplicationState) => ({
    shouldLoadCart: !getCurrentCartState(state).value && !getCurrentCartState(state).isLoading,
    totalCountDisplay: getCurrentCartState(state).value?.totalCountDisplay,
    cartUrl: getPageLinkByPageType(state, "CartPage")?.url,
});

const mapDispatchToProps = {
    loadCurrentCart,
};

export interface CartLinkStyles {
    routerLink?: ClickablePresentationProps;
    iconStyle?: IconPresentationProps;
    titleTypography?: TypographyPresentationProps;
    compactCountText?: TypographyPresentationProps;
}

export const cartLinkStyles: CartLinkStyles = {
    routerLink: {
        css: css`
            padding-bottom: 5px;
            position: relative;
            border: 2px solid transparent;
            top: 5px;
            :hover {
                border-bottom: 2px solid black;
            }
            display: inline-block;
            text-align: center;
            padding-left: 7px;
            padding-right: 7px;
        `,
    },
    iconStyle: {
        color: "text.main",
        css: css`
            display: block;
            margin: auto;
            height: 30px;
            width: 30px;
        `,
    },
    titleTypography: {
        css: css`
            white-space: nowrap;
            display: block;
            font-weight: 500;
            color: black;
            margin-top: 5px;
        `,
        size: 12,
        ellipsis: true,
        transform: "none",
        color: "text.main",
    },
    compactCountText: {
        css: css`
            font-weight: normal;
        `,
    },
};

const iconMap = {
    ausCart: AusCart,
    none: undefined,
} as const;

interface OwnProps extends WidgetProps {
    fields: {
        [fields.visibilityState]: "both" | "label" | "icon";
        [fields.icon]: keyof typeof iconMap;
    };
    isCompactHeaderCart?: boolean;
    theme: BaseTheme;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

/** Reloading the cart makes `totalCountDisplay` temporarily unavailable, so retain the previous value for better UX. */
let retainedTotalCountToDisplay: number | undefined;

const CartLink: FC<Props> = ({
    shouldLoadCart,
    cartUrl,
    totalCountDisplay,
    fields: { visibilityState, icon: selectedIcon },
    loadCurrentCart,
    isCompactHeaderCart,
    theme,
}) => {
    useEffect(() => {
        if (shouldLoadCart) {
            loadCurrentCart();
        }
    }, [shouldLoadCart]);

    const styles = useMergeStyles("cartLink", cartLinkStyles);

    const showIcon = visibilityState !== "label";
    const showLabel = visibilityState !== "icon";

    if (typeof totalCountDisplay !== "undefined") {
        retainedTotalCountToDisplay = totalCountDisplay;
    }

    const icon = iconMap[(showIcon && selectedIcon) || "none"];

    const { routerLink } = styles;

    if (isCompactHeaderCart) {
        return (
            <Link
                {...routerLink}
                color={theme.header.linkColor}
                href={cartUrl}
                icon={{ ...routerLink?.icon, iconProps: { src: icon, ...routerLink?.icon?.iconProps } }}
                data-test-selector="cartLink"
            >
                <Typography as="span" {...styles.compactCountText}>
                    {retainedTotalCountToDisplay}
                </Typography>
                <VisuallyHidden>{siteMessage("Header_CartCount")}</VisuallyHidden>
            </Link>
        );
    }

    // [B2BES] Using clickable over Link to be consistent with other buttons in site header
    return (
        <Clickable {...routerLink} href={cartUrl} data-test-selector="cartLink">
            <Icon {...styles.iconStyle} size={30} src={icon} />
            {showLabel && (
                <Typography {...styles.titleTypography}>
                    {showLabel && `${translate("Cart")} `}(
                    <span data-test-selector="cartLinkQuantity">{retainedTotalCountToDisplay}</span>)
                </Typography>
            )}
            <VisuallyHidden>{siteMessage("Header_CartCount")}</VisuallyHidden>
        </Clickable>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withTheme(CartLink)),
    definition: {
        displayName: "Cart",
        icon: "cart-shopping",
        fieldDefinitions: [
            {
                name: fields.visibilityState,
                displayName: "Settings",
                editorTemplate: "RadioButtonsField",
                options: [
                    {
                        displayName: "Show Both",
                        value: "both",
                    },
                    {
                        displayName: "Show Label Only",
                        value: "label",
                    },
                    {
                        displayName: "Show Icon Only",
                        value: "icon",
                    },
                ],
                defaultValue: "both",
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.icon,
                displayName: "Shopping Cart Icon",
                editorTemplate: "DropDownField",
                options: [
                    {
                        displayName: "Aus Cart",
                        value: "ausCart",
                    },
                ],
                defaultValue: "cart",
                fieldType: "General",
                sortOrder: 2,
            },
        ],
        group: "Common",
    },
};

export default widgetModule;
